/**
 * Dizzaroo Web UI Addendum v0.1 – Design Tokens (Digital)
 * Single source of truth for colors, spacing, radius, shadows, breakpoints.
 * Use these tokens everywhere; no raw hex or arbitrary values in feature code.
 */

// ─── Brand & Primary (from Brand Manual) ─────────────────────────────────────
export const colors = {
  // Primary – Deep Blue
  primary: {
    500: '#1E73BE',  // main brand / primary actions
    600: '#1E6091',  // darker/deeper shade
    light: '#4A8FD4',
    dark: '#1E6091',
  },
  // Accent
  accent: {
    green: '#76C893',   // Soft Green
    teal: '#168AAD',    // Blue Green
    tealLight: '#168AAD',
    tealDark: '#168AAD',
  },
  // Neutrals
  neutral: {
    900: '#1a1a1a',    // primary text
    700: '#4a4a4a',    // secondary text
    500: '#737373',
    200: '#e5e5e5',    // borders, dividers
    100: '#f5f5f5',    // surface / background
    50: '#fafafa',
  },
  // Semantic
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#dc2626',
  info: '#1E73BE',
};

// ─── Spacing (single global scale, in px) ───────────────────────────────────
export const space = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 24,
  6: 32,
  7: 40,
  8: 48,
};

// ─── Border radius ──────────────────────────────────────────────────────────
export const radius = {
  sm: 4,
  md: 8,   // default buttons, inputs, small cards
  lg: 12,
  xl: 16,  // large cards, panels
};

// ─── Shadow / Elevation ─────────────────────────────────────────────────────
export const shadow = {
  none: 'none',
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
  // Directional shadow for sticky headers / app bars
  header: '0 1px 3px 0 rgb(16 24 40 / 0.06), 0 1px 2px 0 rgb(16 24 40 / 0.04)',
};

// ─── Motion ─────────────────────────────────────────────────────────────────
export const transition = {
  fast: '120ms',
  base: '180ms',
  slow: '280ms',
  easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
};

// ─── Focus ring (keyboard accessibility) ────────────────────────────────────
export const focusRing = {
  width: 2,
  color: '#1E73BE',
  offset: 2,
  boxShadow: '0 0 0 2px #ffffff, 0 0 0 4px rgba(30, 115, 190, 0.5)',
};

// ─── Status → color mapping (single source of truth for badges/chips) ───────
// Soft/pastel enterprise-SaaS badge style: tinted background + darker text,
// rather than solid MUI chip colors, for a calmer, more premium look.
export const statusColors = {
  draft:          { bg: '#FFF6E5', color: '#9A5B00', border: '#FCE2AD' },
  pending:        { bg: '#FFF6E5', color: '#9A5B00', border: '#FCE2AD' },
  in_review:      { bg: '#EAF1FE', color: '#1E56A0', border: '#C7DBFB' },
  submitted:      { bg: '#EAF1FE', color: '#1E56A0', border: '#C7DBFB' },
  approved:       { bg: '#E7F7ED', color: '#146C2E', border: '#BCE8CB' },
  active:         { bg: '#E7F7ED', color: '#146C2E', border: '#BCE8CB' },
  completed:      { bg: '#E7F7ED', color: '#146C2E', border: '#BCE8CB' },
  rejected:       { bg: '#FDECEC', color: '#B3261E', border: '#F6C6C4' },
  error:          { bg: '#FDECEC', color: '#B3261E', border: '#F6C6C4' },
  not_initiated:  { bg: '#F1F1F2', color: '#5F6368', border: '#E1E2E4' },
  inactive:       { bg: '#F1F1F2', color: '#5F6368', border: '#E1E2E4' },
  default:        { bg: '#F1F1F2', color: '#5F6368', border: '#E1E2E4' },
};

// ─── Breakpoints (shared across all apps) ────────────────────────────────────
export const breakpoints = {
  values: {
    xs: 0,
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
  },
};

// ─── Typography (web sizes, Urbanist) ───────────────────────────────────────
export const typography = {
  fontFamily: '"Urbanist", "Inter", "SF Pro Display", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
  // Page title: 28–32px, 900
  pageTitle: { fontSize: '1.75rem', fontWeight: 900 },   // 28px
  // Section: 22–24px, 700
  sectionHeading: { fontSize: '1.375rem', fontWeight: 700 },  // 22px
  // Sub-heading: 18–20px, 600
  subheading: { fontSize: '1.125rem', fontWeight: 600 },      // 18px
  // Body: 14–16px, 400–500
  body: { fontSize: '0.875rem', fontWeight: 400 },             // 14px
  bodyMedium: { fontSize: '0.9375rem', fontWeight: 500 },     // 15px
  // Caption/Label: 12–13px, 500
  caption: { fontSize: '0.75rem', fontWeight: 500 },
  label: { fontSize: '0.8125rem', fontWeight: 500 },          // 13px
};

export default {
  colors,
  space,
  radius,
  shadow,
  transition,
  focusRing,
  statusColors,
  breakpoints,
  typography,
};
